import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { BestSellerProvider } from "@/context/BestSellerContext";
import AgeGate from "@/components/AgeGate";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import Concierge from "@/components/Concierge";
import VillaAmbient from "@/components/VillaAmbient";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Auth from "@/pages/Auth";
import Account from "@/pages/Account";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import AuthCallback from "@/pages/AuthCallback";
import Journal from "@/pages/Journal";
import JournalPost from "@/pages/JournalPost";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import Wishlist from "@/pages/Wishlist";
import CompanionChat from "@/pages/CompanionChat";
import GiftConcierge from "@/pages/GiftConcierge";
import NewArrivals from "@/pages/NewArrivals";
import BestSellers from "@/pages/BestSellers";
import Rooms from "@/pages/Rooms";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminDiscounts from "@/pages/admin/AdminDiscounts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminMarketing from "@/pages/admin/AdminMarketing";
import AdminJournal from "@/pages/admin/AdminJournal";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminReports from "@/pages/admin/AdminReports";

function AppRouter() {
  const location = useLocation();
  // Detect OAuth return synchronously during render (CRITICAL)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const isAdminRoute = location.pathname.startsWith("/admin");
  // The splash entrance ("/") is meant to feel like a threshold — no header,
  // no footer, no floating concierge distraction. Everything else keeps chrome.
  const isSplash = location.pathname === "/";
  // The rooms page is a threshold too — it should feel like a moment of
  // choice, not a busy shop. Strip sidebar/footer/concierge there too, so
  // the villa gate + floor plan pair reads as a single arrival sequence.
  const isThreshold = isSplash || location.pathname === "/rooms";
  const mainClass = `min-h-screen ${
    !isAdminRoute && !isThreshold ? "lg:pl-52" : ""
  }`;
  return (
    <>
      {!isAdminRoute && !isThreshold && <Header />}
      <main className={mainClass}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/shop/:category" element={<Shop />} />
          <Route path="/product/:slug" element={<ProductDetail />} />
          <Route path="/cart" element={<Shop />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/account" element={<Account />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/journal/:slug" element={<JournalPost />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/companions/:slug" element={<CompanionChat />} />
          <Route path="/gift" element={<GiftConcierge />} />
          <Route path="/arrivals" element={<NewArrivals />} />
          <Route path="/favourites" element={<BestSellers />} />
          <Route path="/rooms" element={<Rooms />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="discounts" element={<AdminDiscounts />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="marketing" element={<AdminMarketing />} />
            <Route path="journal" element={<AdminJournal />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="suppliers" element={<AdminSuppliers />} />
            <Route path="reports" element={<AdminReports />} />
          </Route>
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      {!isAdminRoute && !isThreshold && <Footer />}
      {!isAdminRoute && !isThreshold && <Concierge />}
      {!isAdminRoute && <VillaAmbient />}
      <CartDrawer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
            <BestSellerProvider>
            <AgeGate />
            <AppRouter />
            <Toaster
              position="bottom-right"
              theme="dark"
              toastOptions={{
                style: {
                  background: "#110e0e",
                  color: "#f5f2eb",
                  border: "1px solid rgba(245,242,235,0.08)",
                  borderRadius: 0,
                },
              }}
            />
            </BestSellerProvider>
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
